import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FAQsListSkeleton from './FAQsListSkeleton';
import ErrorState from '../ErrorState/ErrorState';
import { AnimatedOnScroll } from '../AnimatedOnScroll';
import './FAQs.scss';

const PAGE_TYPES = {
  HOME_PAGE: 'homePage',
  FAQS_PAGE: 'faqPage',
  B2B_PAGE: 'b2bPage',
  B2C_PAGE: 'b2cPage',
  SERV_PAGE: 'servicesPage'
};

const FAQsList = ({ faqs, page, isLoading = false }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  const [activeIndex, setActiveIndex] = useState(null);
  const faqRefs = useRef([]);

  const getLocalizedText = useCallback((textObj) => {
    if (!textObj) return '';

    if (typeof textObj === 'string') return textObj;

    if (textObj && typeof textObj === 'object') {
      if (textObj[currentLang] !== undefined) {
        const text = textObj[currentLang];
        return typeof text === 'string' ? text : String(text);
      }

      if (textObj.en !== undefined) {
        const text = textObj.en;
        return typeof text === 'string' ? text : String(text);
      }

      return '';
    }

    return String(textObj || '');
  }, [currentLang]);

  const toggleFAQ = useCallback((index) => {
    setActiveIndex(prevIndex => prevIndex === index ? null : index);
  }, []);

  const renderTextWithLinks = useCallback((contentObj, className = "answer__text") => {
    const localizedContent = contentObj[currentLang] || contentObj.en || [];
    
    if (!Array.isArray(localizedContent)) {
      return <p className={className}>{getLocalizedText(contentObj)}</p>;
    }

    return (
      <p className={className}>
        {localizedContent.map((part, idx) => {
          if (typeof part === 'string') {
            return <span className="answer__normal" key={idx}>{part}</span>;
          }
          
          if (typeof part === 'object' && part.text && part.link) {
            return (
              <Link key={idx} to={part.link} className="answer__link">
                {part.text}
              </Link>
            );
          }
          
          if (typeof part === 'object' && part.text) {
            return <span key={idx}>{part.text}</span>;
          }
          
          return null;
        })}
      </p>
    );
  }, [currentLang, getLocalizedText]);

  const renderBoldText = useCallback((contentObj) => {
    const localizedContent = contentObj[currentLang] || contentObj.en || [];

    if (!Array.isArray(localizedContent)) {
      return <p className="answer__text">{getLocalizedText(contentObj)}</p>;
    }

    return (
      <p className="answer__text">
        {localizedContent.map((item, idx) => {
          if (typeof item === 'string') {
            return <span key={idx} className="answer__normal">{item}</span>;
          }

          if (typeof item === 'object' && item.bold && item.value) {
            return (
              <span key={idx} className="answer__bold">
                {item.value}
              </span>
            );
          }

          if (typeof item === 'object' && item.value) {
            return <span key={idx} className="answer__normal">{item.value}</span>;
          }

          return null;
        })}
      </p>
    );
  }, [currentLang, getLocalizedText]);

  const renderAnswer = useCallback((answer) => {
    if (!answer || !Array.isArray(answer.content)) return null;

    return answer.content.map((block, idx) => {
      const localizedText = getLocalizedText(block.text);

      if (block.type === 'title' && localizedText) {
        return (
          <h4 key={idx} className="answer__title">
            {localizedText}
          </h4>
        );
      }

      if (block.type === 'subtitle' && localizedText) {
        return (
          <h5 key={idx} className="answer__subtitle">
            {localizedText}
          </h5>
        );
      }

      if (block.type === 'text' && localizedText) {
        if (Array.isArray(block.text[currentLang] || block.text.en)) {
          return (
            <React.Fragment key={idx}>
              {renderTextWithLinks(block.text)}
            </React.Fragment>
          );
        }

        if (Array.isArray(localizedText)) {
          return (
            <React.Fragment key={idx}>
              {localizedText.map((paragraph, pIdx) => (
                <p key={`${idx}-${pIdx}`} className="answer__text">
                  {paragraph}
                </p>
              ))}
            </React.Fragment>
          );
        }

        return (
          <p key={idx} className="answer__text">
            {localizedText}
          </p>
        );
      }

      if (block.type === 'boldtext' && block.content) {
        return (
          <React.Fragment key={idx}>
            {renderBoldText(block.content)}
          </React.Fragment>
        );
      }

      if (block.type === 'list' && block.items) {
        return (
          <ul key={idx} className="answer__list">
            {block.items.map((item, listIdx) => {
              if (Array.isArray(item[currentLang] || item.en)) {
                const localizedContent = item[currentLang] || item.en || [];

                return (
                  <li key={listIdx} className="answer__list__item">
                    {localizedContent.map((part, partIdx) => {
                      if (typeof part === 'string') {
                        return <span className='span__normal' key={partIdx}>{part}</span>;
                      }

                      if (typeof part === 'object' && part.text && part.link) {
                        return (
                          <Link key={partIdx} to={part.link} className="answer__link">
                            {part.text}
                          </Link>
                        );
                      }

                      if (typeof part === 'object' && part.text) {
                        return <span key={partIdx}>{part.text}</span>;
                      }

                      return null;
                    })}
                  </li>
                );
              }

              const localizedItem = getLocalizedText(item);
              return (
                <li key={listIdx} className="answer__list__item">
                  {localizedItem}
                </li>
              );
            })}
          </ul>
        );
      }

      return null;
    });
  }, [getLocalizedText, renderTextWithLinks, renderBoldText, currentLang]);

  const filteredFAQs = useMemo(() => {
    if (!faqs || !Array.isArray(faqs) || faqs.length === 0) {
      return [];
    }

    return faqs
      .filter(faq => {
        const pageKey = page.charAt(0).toLowerCase() + page.slice(1);
        
        if (faq.visibility && faq.visibility[pageKey] !== undefined) {
          return faq.visibility[pageKey];
        }
        
        return false;
      })
      .sort((a, b) => {
        const pageKey = page.charAt(0).toLowerCase() + page.slice(1);
        
        const priorityA = a.priority && a.priority[pageKey] !== undefined ? a.priority[pageKey] : 999;
        const priorityB = b.priority && b.priority[pageKey] !== undefined ? b.priority[pageKey] : 999;
        
        return priorityA - priorityB;
      });
  }, [faqs, page]);

  const shouldShowNumbers = useMemo(() => 
    ![PAGE_TYPES.HOME_PAGE, PAGE_TYPES.B2B_PAGE, PAGE_TYPES.B2C_PAGE, PAGE_TYPES.SERV_PAGE, PAGE_TYPES.FAQS_PAGE].includes(page),
    [page]
  );

  if (isLoading) {
    return <FAQsListSkeleton />;
  }

  if (!faqs || !Array.isArray(faqs) || faqs.length === 0) {
    return (
      <div className="faqs__section">
        <ErrorState />
      </div>
    );
  }

  if (filteredFAQs.length === 0) {
    return (
      <div className="faqs__section">
        <ErrorState />
      </div>
    );
  }

  return (
    <div className={`faqs__section ${page === PAGE_TYPES.FAQS_PAGE ? 'no-padding' : ''}`}>
      {filteredFAQs.map((faq, index) => {
        const localizedQuestion = getLocalizedText(faq.question);
        const isActive = activeIndex === index;

        return (
          <AnimatedOnScroll animation="fade-up" delay={0} key={index}>
          <div
            className={`faq__item ${isActive ? 'active' : ''}`}
            key={faq.id || index}
          >
            <h3
              className={`faq__question ${isActive ? 'active' : ''}`}
              onClick={() => toggleFAQ(index)}
            >
              {shouldShowNumbers && (
                <span className="faq__number">
                  {String(index + 1).padStart(2, '0')}
                </span>
              )}
              {localizedQuestion}
              <span className="faq__arrow" />
            </h3>
            <div
              className="faq__answer"
              ref={(el) => (faqRefs.current[index] = el)}
              style={{
                height: isActive ? `${faqRefs.current[index]?.scrollHeight}px` : '0',
                transition: 'height 0.3s ease',
                overflow: 'hidden',
                pointerEvents: isActive ? 'auto' : 'none',
              }}
            >
              <div className="answer__content">{renderAnswer(faq.answer)}</div>
            </div>
          </div>
          </AnimatedOnScroll>
        );
      })}
    </div>
  );
};

export default React.memo(FAQsList);